// AP-173 · Novo Fluxo Inteligente de Envio de Documentos
// Modal em 4 passos: Cliente (autocomplete) → Processo → Documento → Sucesso.
// Reusa /customers/search (busca por nome/whatsapp/processo com preview inline).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import {
  Search, Upload, UploadCloud, Loader2, Check, FileText, User as UserIcon,
  Phone, FolderKanban, CheckCircle2, ArrowLeft, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const MAX_UPLOAD = 15 * 1024 * 1024;
const CATEGORY_OPTIONS = [
  { value: "general",   label: "Geral" },
  { value: "identity",  label: "Identidade" },
  { value: "contract",  label: "Contrato" },
  { value: "receipt",   label: "Recibo" },
  { value: "proof",     label: "Comprovante" },
  { value: "medical",   label: "Médico" },
  { value: "legal",     label: "Jurídico" },
  { value: "financial", label: "Financeiro" },
];
const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }); }
  catch { return iso; }
};

export default function SmartUploadDialog({ open, onOpenChange, onUploaded, defaultCustomer }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep]           = useState(1); // 1-client · 2-process · 3-document · 4-success
  const [customer, setCustomer]   = useState(defaultCustomer || null);
  const [proc, setProc]           = useState(null);
  const [procs, setProcs]         = useState([]);
  const [file, setFile]           = useState(null);
  const [docName, setDocName]     = useState("");
  const [category, setCategory]   = useState("general");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [uploaded, setUploaded]   = useState(null);
  const [closedWarn, setClosedWarn] = useState(false);

  const resetAll = () => {
    setStep(1); setCustomer(null); setProc(null); setProcs([]);
    setFile(null); setDocName(""); setCategory("general");
    setDescription(""); setUploading(false); setProgress(0);
    setUploaded(null); setClosedWarn(false);
  };
  useEffect(() => { if (!open) resetAll(); }, [open]);

  // ---------- Carregamento automático de processos ao escolher cliente ----------
  useEffect(() => {
    if (!customer) return;
    const previewed = customer.processes_preview || [];
    setProcs(previewed);
    // Caso único → auto-seleciona.
    if (previewed.length === 1) {
      setProc(previewed[0]);
      setStep(3);
    } else {
      setStep(2);
    }
  }, [customer]);

  const canUpload = file && customer && proc && docName.trim();

  const doUpload = async () => {
    if (!canUpload) return;
    if (proc.closed) { setClosedWarn(true); return; }
    await _upload();
  };
  const _upload = async () => {
    setUploading(true); setProgress(5);
    try {
      // Backend contract (/processes/{pid}/documents) expects JSON body with
      // file_name, mime_type, file_data_url, size, category, description.
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
      });
      setProgress(30);
      // Preserve extension from original file when user customised docName.
      const ext = (file.name.match(/\.[^.]+$/) || [""])[0];
      const name = docName.trim() || file.name.replace(/\.[^/.]+$/, "");
      const file_name = name.toLowerCase().endsWith(ext.toLowerCase()) ? name : `${name}${ext}`;
      const { data } = await api.post(`/processes/${proc.id}/documents`, {
        file_name,
        mime_type: file.type || "application/octet-stream",
        file_data_url: dataUrl,
        size: file.size,
        category,
        description,
      }, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.min(95, Math.round(30 + (e.loaded / e.total) * 65)));
        },
      });
      setProgress(100);
      setUploaded({
        doc: data,
        at: new Date().toISOString(),
        uploader: user?.full_name || user?.email || "—",
      });
      setStep(4);
      onUploaded?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setUploading(false); setProgress(0);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl" data-testid="smart-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {step === 4 ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Upload className="h-5 w-5 text-primary" />}
              {step === 4 ? "Documento enviado" : "Anexar Documento"}
            </DialogTitle>
            {step < 4 && <DialogDescription>Passo {step} de 3 — {["Cliente","Processo","Documento"][step-1]}</DialogDescription>}
          </DialogHeader>

          {step === 1 && (
            <ClientStep onPick={setCustomer} />
          )}

          {step === 2 && customer && (
            <ProcessStep
              customer={customer}
              processes={procs}
              onPick={(p) => { setProc(p); setStep(3); }}
              onBack={() => { setCustomer(null); setStep(1); }}
            />
          )}

          {step === 3 && customer && proc && (
            <DocumentStep
              customer={customer} proc={proc}
              file={file} setFile={setFile}
              docName={docName} setDocName={setDocName}
              category={category} setCategory={setCategory}
              description={description} setDescription={setDescription}
              uploading={uploading} progress={progress}
              onBack={() => (procs.length > 1 ? setStep(2) : (setCustomer(null), setStep(1)))}
              onSubmit={doUpload}
              canUpload={canUpload}
            />
          )}

          {step === 4 && uploaded && (
            <SuccessStep
              customer={customer} proc={proc} doc={uploaded}
              onNew={() => resetAll()}
              onOpenProc={() => { onOpenChange(false); navigate(`/processes/${proc.id}`); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* AP-173 · Segurança: processo encerrado requer confirmação do Master */}
      <AlertDialog open={closedWarn} onOpenChange={setClosedWarn}>
        <AlertDialogContent data-testid="smart-upload-closed-warn">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Processo encerrado
            </AlertDialogTitle>
            <AlertDialogDescription>
              O processo <b>{proc?.process_number}</b> está marcado como encerrado.
              Deseja mesmo anexar um documento? Esta ação será registrada no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setClosedWarn(false); _upload(); }}
                                data-testid="smart-upload-closed-confirm"
                                className="bg-amber-600 hover:bg-amber-700">
              Confirmar e Anexar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============== STEP 1 · CLIENTE (autocomplete) ==============
function ClientStep({ onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // AP-173 · only_with_processes garante que clientes sem processos
        // (fantasmas / recém cadastrados) NUNCA aparecem no fluxo de upload.
        const { data } = await api.get("/customers/search", {
          params: { q: q.trim(), limit: 20, only_with_processes: true },
        });
        setItems(data?.items || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 220);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Digite nome, WhatsApp ou número do processo…"
                className="pl-9" data-testid="smart-upload-client-search" />
      </div>
      <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-1.5"
           data-testid="smart-upload-client-results">
        {loading && (
          <div className="text-center py-4 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Buscando…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            {q ? "Nenhum cliente encontrado." : "Digite para buscar o cliente."}
          </div>
        )}
        {!loading && items.map((c) => (
          <button key={c.id} type="button"
                    onClick={() => onPick(c)}
                    data-testid={`smart-upload-client-${c.id}`}
                    className="w-full text-left rounded-xl border border-border px-3 py-2.5 hover:border-primary/40 hover:bg-primary/5 transition-all active:scale-[0.99]">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-semibold text-sm flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5 text-primary" /> {c.full_name}
              </p>
              <Badge variant="outline" className="text-[10px]">
                {c.processes_count || 0} processo{(c.processes_count || 0) === 1 ? "" : "s"}
              </Badge>
            </div>
            {c.whatsapp && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                <Phone className="h-3 w-3" /> {c.whatsapp}
              </p>
            )}
            {(c.processes_preview || []).slice(0, 2).map((p) => (
              <p key={p.id} className="text-[11px] text-muted-foreground mt-0.5 truncate">
                📄 <span className="font-mono text-primary">{p.process_number}</span>
                {p.service_name ? ` · ${p.service_name}` : ""}
                {p.status ? ` · ${p.status}` : ""}
              </p>
            ))}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============== STEP 2 · PROCESSO ==============
function ProcessStep({ customer, processes, onPick, onBack }) {
  if (!processes.length) {
    return (
      <div className="space-y-3">
        <div className="text-center py-6 text-sm text-muted-foreground">
          Este cliente ainda não possui processos cadastrados.
        </div>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Trocar cliente
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-primary/5 border border-primary/30 px-3 py-2 text-xs flex items-center justify-between gap-2">
        <span>Cliente: <b>{customer.full_name}</b></span>
        <Button variant="ghost" size="sm" onClick={onBack}
                data-testid="smart-upload-change-client">
          <X className="h-3.5 w-3.5" /> Trocar
        </Button>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="smart-upload-process-list">
        {processes.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)}
                    data-testid={`smart-upload-process-${p.id}`}
                    className="w-full text-left rounded-xl border border-border px-3 py-2.5 hover:border-primary/40 hover:bg-primary/5 transition-all">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-mono text-xs text-primary font-semibold">{p.process_number}</p>
              <Badge variant="outline" className={`text-[10px] ${p.closed ? "border-slate-300 text-slate-500" : "border-emerald-300 text-emerald-700"}`}>
                {p.status || "—"}
              </Badge>
            </div>
            <p className="text-sm mt-0.5 truncate flex items-center gap-1">
              <FolderKanban className="h-3.5 w-3.5 text-muted-foreground" />
              {p.service_name || "—"}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============== STEP 3 · DOCUMENTO ==============
function DocumentStep({ customer, proc, file, setFile, docName, setDocName,
                        category, setCategory, description, setDescription,
                        uploading, progress, onBack, onSubmit, canUpload }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const validateAndSet = (f) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD) { toast.error("Arquivo > 15 MB"); return; }
    setFile(f);
    if (!docName) setDocName(f.name.replace(/\.[^/.]+$/, ""));
  };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    validateAndSet(e.dataTransfer.files?.[0]);
  };
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-primary/5 border border-primary/30 px-3 py-2 text-xs space-y-0.5">
        <p>Cliente: <b>{customer.full_name}</b></p>
        <p>Processo: <span className="font-mono text-primary">{proc.process_number}</span> · {proc.service_name || "—"}</p>
      </div>
      <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed px-4 py-6 text-center cursor-pointer transition ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
            data-testid="smart-upload-dropzone">
        <UploadCloud className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
        {file ? (
          <>
            <p className="text-sm font-medium truncate">{file.name}</p>
            <p className="text-[11px] text-muted-foreground">{fmtSize(file.size)}</p>
          </>
        ) : (
          <>
            <p className="text-sm">Arraste um arquivo ou clique</p>
            <p className="text-[11px] text-muted-foreground">PDF, imagem, DOC/DOCX · máx. 15 MB</p>
          </>
        )}
        <input ref={inputRef} type="file" className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.txt,application/pdf,image/*"
                onChange={(e) => validateAndSet(e.target.files?.[0])}
                data-testid="smart-upload-file-input" />
      </div>
      <div>
        <Label className="text-xs">Nome do documento *</Label>
        <Input value={docName} onChange={(e) => setDocName(e.target.value)}
                placeholder="Ex.: Comprovante de residência"
                data-testid="smart-upload-doc-name" className="mt-1" />
      </div>
      <div>
        <Label className="text-xs">Categoria</Label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="mt-1 block w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                data-testid="smart-upload-category">
          {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs">Observações (opcional)</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={2} data-testid="smart-upload-description" className="mt-1"
                    maxLength={500} placeholder="Breve descrição…" />
      </div>
      {uploading && (
        <div data-testid="smart-upload-progress">
          <div className="flex items-center justify-between mb-1 text-xs text-muted-foreground">
            <span>Enviando…</span><span className="tabular-nums">{progress}%</span>
          </div>
          <Progress value={progress} />
        </div>
      )}
      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onBack} disabled={uploading}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <Button onClick={onSubmit} disabled={!canUpload || uploading}
                data-testid="smart-upload-submit">
          {uploading
            ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enviando…</>
            : <><Upload className="h-4 w-4 mr-1" /> Enviar documento</>}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ============== STEP 4 · SUCESSO ==============
function SuccessStep({ customer, proc, doc, onNew, onOpenProc }) {
  return (
    <div className="space-y-4" data-testid="smart-upload-success">
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <p className="font-bold text-emerald-900">✅ Documento enviado com sucesso.</p>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <div><dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Cliente</dt><dd className="text-emerald-950">{customer.full_name}</dd></div>
          <div><dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Processo</dt><dd className="text-emerald-950 font-mono">{proc.process_number}</dd></div>
          <div className="sm:col-span-2"><dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Documento</dt><dd className="text-emerald-950 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {doc.doc?.file_name || doc.doc?.name || doc.doc?.filename || "—"}</dd></div>
          <div><dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Data</dt><dd className="text-emerald-950 tabular-nums">{fmtDT(doc.at)}</dd></div>
          <div><dt className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Enviado por</dt><dd className="text-emerald-950">{doc.uploader}</dd></div>
        </dl>
      </div>
      <DialogFooter className="gap-2 flex-col sm:flex-row">
        <Button variant="outline" onClick={onNew} data-testid="smart-upload-new">
          <Upload className="h-4 w-4 mr-1" /> Enviar outro Documento
        </Button>
        <Button onClick={onOpenProc} data-testid="smart-upload-open-proc">
          <FolderKanban className="h-4 w-4 mr-1" /> Abrir Processo
        </Button>
      </DialogFooter>
    </div>
  );
}
